// 조직도 좌표 계산(design/manage.js layoutOrg 포팅). 사용자는 좌표를 정하지 않는다.
// 부서(열) × 직급 단계(줄)만 정하면 위치가 계산된다. viewBox -200~200 기준.
// 외부 조직(고객사)은 '회사 열'로 내 회사 부서 열 옆에 붙는다(다른 색·영역).
interface P { id: string; dept?: string | null; level?: number; company?: string | null }
interface D { id: string; name: string }
interface C { id: string; name: string }

export interface DeptRect { name: string; x: number; y: number; w: number; h: number; external?: boolean; color?: string }
export interface OrgLayout { orgOf: Record<string, [number, number]>; deptRects: DeptRect[]; meOrg: [number, number] }

const ROW: Record<number, number> = { 1: -165, 2: -70, 3: 5, 4: 80, 5: 150 }
// 고객사별 영역 색(순번). 내 회사와 구분되게 중립·흙빛 계열
export const EXT_COLORS = ['#8C8678', '#5B8A72', '#7A8396', '#A07C5B', '#8A5B7A', '#6B7B8A']
export const companyColor = (companies: { id: string }[], id?: string | null) => {
  const i = id ? companies.findIndex(c => c.id === id) : -1
  return i >= 0 ? EXT_COLORS[i % EXT_COLORS.length] : undefined
}

export function layoutOrg(people: P[], depts: D[], me: { dept?: string | null; level?: number }, companies: C[] = []): OrgLayout {
  const all = people.map(p => ({ id: p.id, dept: p.dept ?? null, lv: p.level || 4, company: p.company ?? null }))
  all.push({ id: 'me', dept: me.dept ?? null, lv: me.level || 4, company: null })
  const inDept = (a: { dept: string | null }) => !!a.dept && depts.some(d => d.id === a.dept)
  const external = (a: { company: string | null }) => !!a.company && companies.some(c => c.id === a.company)
  const pos: Record<string, [number, number]> = {}

  // 열 = 내 회사 부서들 + 외부 회사들(각 회사가 하나의 열)
  type Col = { kind: 'dept' | 'company'; id: string; name: string; color?: string }
  const columns: Col[] = [
    ...depts.map(d => ({ kind: 'dept' as const, id: d.id, name: d.name })),
    ...companies.map((c, ci) => ({ kind: 'company' as const, id: c.id, name: c.name, color: EXT_COLORS[ci % EXT_COLORS.length] })),
  ]

  // 상단 행: 내 회사의 소속 없는 사람 + 나 (외부 사람은 제외)
  const top = all.filter(a => !external(a) && !inDept(a))
  top.forEach((a, i) => { pos[a.id] = [(i - (top.length - 1) / 2) * 80, ROW[1]] })

  const n = Math.max(columns.length, 1), W = 384, colW = W / n, x0 = -192
  const deptRects: DeptRect[] = columns.map((col, k) => {
    const cx = x0 + colW * (k + 0.5)
    const members = all.filter(a => col.kind === 'dept' ? (a.dept === col.id && !external(a)) : a.company === col.id)
    const byLv: Record<number, typeof all> = {}
    members.forEach(a => { const lv = Math.max(2, a.lv); (byLv[lv] = byLv[lv] || []).push(a) })
    let maxY = -70
    for (const [lvS, arr] of Object.entries(byLv)) {
      const lv = +lvS
      const gap = Math.max(62, Math.min(78, (colW - 24) / Math.max(arr.length, 1)))
      const zig = gap < 72 && arr.length > 1 // 칸이 좁아 이름표가 겹치면 지그재그
      arr.forEach((a, i) => {
        const x = cx + (i - (arr.length - 1) / 2) * gap
        const y = ROW[lv] + (zig ? (i % 2 ? 20 : -14) : 0)
        pos[a.id] = [x, y]; maxY = Math.max(maxY, y)
      })
    }
    return {
      name: col.name, x: cx - colW / 2 + 6, y: -112, w: colW - 12, h: maxY + 52 + 112,
      external: col.kind === 'company', color: col.color,
    }
  })

  return { orgOf: pos, deptRects, meOrg: pos['me'] || [0, 0] }
}
